import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { apiColletions } from "src/app/core/constants/api_collection";
import { ApiService } from "./api.service";
import { Helper } from "src/app/core/helpers/helper";
import { AdminService } from "./admin.service";
import { SocketService } from "./socket.service";
import { DispatcherScopeService } from "src/app/core/services/dispatcher-scope.service";

@Injectable({
  providedIn: "root"
})
export class AuthService {
  public logginUserToken: any;
  public loginSubject = new BehaviorSubject<any>(null);
  loginObservable: Observable<any> = this.loginSubject.asObservable();
  private permissions = new BehaviorSubject<any>(null);
  authPermission = this.permissions.asObservable();
  isMainAdminLogin: boolean = false;
  isScriptLoaded: boolean = false;

  get isAuthenticated(): boolean {
    return !this.logginUserToken || this.logginUserToken === null;
  }

  constructor(private apiService: ApiService, private helper: Helper, private adminService: AdminService, private socketService: SocketService, private dispatcherScope: DispatcherScopeService) {}

  getToken(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.getToken, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next({ data: response.data, token: response.token });
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  login(parameters: any): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.apiService.post({ url: apiColletions.signin, parameters }).subscribe({
        next: (response) => {
          if (response.token) {
            this.logginUserToken = response.token;
            this.helper.userToken = this.logginUserToken;
            localStorage.setItem("userToken", this.logginUserToken);
            this.loginSubject.next(this.logginUserToken);
            this.autologin().then(() => {
              observer.next(response.data);
              observer.complete();
            });
            return;
          }

          // A 200 WITHOUT a token is not a successful login.
          //
          // The server answers a 2FA-enabled account with 200 +
          // { message: TWO_FACTOR_VERIFICATION_NEEDED, data: { twoFactorAuth: true } }
          // and no token. This used to fall through to `observer.next(response.data)`,
          // and `data` is a truthy object — so the caller treated it as success and
          // routed to "/" with nothing stored. The guard bounced straight back to the
          // login screen and the whole thing looked like "nothing happens".
          //
          // The dispatcher panel has no 2FA screen at all (2FA was built for the admin
          // panel), so the only honest outcome here is to say so.
          if (response.data?.twoFactorAuth) {
            observer.error({
              code: "TWO_FACTOR_NOT_SUPPORTED",
              message: "Two-factor authentication is enabled on this account, but the dispatcher panel does not support it yet. Ask an administrator to turn 2FA off for this account."
            });
            return;
          }

          // Any other token-less 200: report failure rather than a phantom success.
          observer.next(false);
          observer.complete();
        },
        error: (err: any) => {
          const code = err?.error?.message;
          if (code === "NO_CITIES_ASSIGNED") {
            // Surface specific error so login UI can show a clear message.
            observer.error({ code, message: "Your account has no cities assigned. Contact your administrator." });
            return;
          }
          if (code) {
            // Everything else the server named — blocked account, bad credentials,
            // account not found. Passing the code up beats a silent `false`.
            observer.error({ code, message: err?.error?.dynamicData ? code : code });
            return;
          }
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  autologin(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (localStorage.getItem("userToken")) {
        this.logginUserToken = localStorage.getItem("userToken");
      }
      const parameters: any = { isShowSuccessToast: false };
      this.adminService.getDetails(parameters).subscribe((response: any) => {
        this.helper.getCorporateDetail.next(response);
        this.helper.minPhoneNumberLength = response.setting.minPhoneLength;
        this.helper.maxPhoneNumberLength = response.setting.maxPhoneLength;
        this.helper.secOtpResendInterval = response.setting.entitySetting.secOtpResendInterval;
        this.helper.adminSettings = response.setting;
        this.helper.MapBoxAccessToken = this.helper.adminSettings.mapKey;
        this.loadScripts();
        if (this.logginUserToken) {
          const roleReady = this.applyDispatcherRole(response?.entity?.roleId);
          this.loginSubject.next(this.logginUserToken);
          if (!this.socketService.socket) {
            this.socketService.setupSocketConnection();
            this.connectSocket();
          }
          this.dispatcherScope.load().subscribe({
            complete: () => {
              roleReady.then(() => resolve());
            },
            error: (e: any) => {
              console.error("[auth] failed to load dispatcher cities", e);
              roleReady.then(() => resolve());
            }
          });
          return;
        } else {
          this.logginUserToken = null;
          this.loginSubject.next(this.logginUserToken);
          localStorage.removeItem("userToken");
          this.helper.isUpadtedlocalStorage();
          this.helper.route.navigate(["/auth/login"]);
        }
        resolve();
      });
    });
  }

  /**
   * Applies the dispatcher's role, if they have one.
   *
   * No `roleId` means full panel access — the same rule the server enforces, where a missing roleId makes
   * the permission check pass through. That is what keeps every dispatcher account created before roles
   * existed working untouched, and makes roles opt-in per account.
   *
   * With a role, the granted pages become `permissions` and `isMainAdmin` goes false, which is what the
   * sidebar filter, the route guard and `helper.hasPermission` all key off. Note these are advisory: the
   * server checks the same role on every request, so a stale panel cannot grant anything.
   */
  private applyDispatcherRole(roleId?: string): Promise<void> {
    if (!roleId) {
      this.helper.isMainAdmin = true;
      this.isMainAdminLogin = true;
      this.permissions.next(null);
      this.helper.permissions = [];
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.adminService.getDispatcherRoleDetails(roleId).subscribe({
        next: (response: any) => {
          // No blanket "on any problem, grant everything" fallback here on purpose. The account HAS a role,
          // so the server will gate it regardless; quietly restoring the full menu would only produce a
          // sidebar full of tabs that 403 on click, and would hide a broken role behind what looks like
          // normal behaviour. An empty sidebar is the honest signal that the role failed to load.
          if (!response) {
            console.error("[auth] could not load dispatcher role", roleId);
          } else if (!response?.dispatcherRole?.urls?.length) {
            console.error("[auth] dispatcher role grants no pages", roleId);
          }

          const urls = response?.dispatcherRole?.urls ?? [];

          this.helper.isMainAdmin = false;
          this.isMainAdminLogin = false;
          this.helper.permissions = urls;
          this.permissions.next(urls);
          resolve();
        },
        error: () => {
          this.helper.isMainAdmin = false;
          this.isMainAdminLogin = false;
          this.helper.permissions = [];
          this.permissions.next([]);
          resolve();
        }
      });
    });
  }

  loadScripts(): Promise<any> {
    return new Promise((resolve) => {
      if (!this.isScriptLoaded) {
        this.adminService.getDetails({ isShowSuccessToast: false }).subscribe((response: any) => {
          if (response) {
            this.isScriptLoaded = true;
            const mapkey: string = response.setting?.mapKey ?? "";
            this.helper.mapKey = mapkey ?? "";
            // Pin to v3.64: DrawingManager (and the drawing/visualization libraries) were removed in Maps JS API v3.65.
            // See https://developers.google.com/maps/deprecations. Temporary — migrate to custom polygon drawing before v3.64 is retired.
            this.helper.loadGoogleScript(`https://maps.googleapis.com/maps/api/js?key=${mapkey}&v=3.64&libraries=places,drawing,geometry,visualization,marker`);
          }
        });
        resolve(true);
      } else {
        resolve(true);
      }
    });
  }

  /** Clears every stored panel value (token, menus, caller history, prefs) and reloads on the login page. */
  private clearLocalSession(): void {
    localStorage.removeItem("userToken");
    localStorage.clear();
    sessionStorage.clear();
    this.dispatcherScope.clear();
    this.helper.isUpadtedlocalStorage();
    this.helper.route.navigate(["/auth/login"]).then(() => {
      window.location.reload();
    });
  }

  logOut(parameters: any): Observable<boolean> {
    return new Observable<any>((observer) => {
      this.apiService.post({ url: apiColletions.signout, parameters }).subscribe({
        next: (response) => {
          // SECURITY: always end the session in this browser, even when the server call fails or
          // the token is already dead; otherwise a "logged out" workstation keeps a live token.
          this.clearLocalSession();
          observer.next(response.data ?? false);
          observer.complete();
        },
        error: () => {
          this.clearLocalSession();
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  connectSocket(): void {
    this.socketService.connectSocket((isConnected) => {
      if (isConnected) {
        const param = { event: "SIGN_UP" };
        this.socketService.sendAndListenEvent(this.helper.SocketEvents.SIGN_UP, param, () => {});
      }
    });
  }
}
